import { CircleX, Grip } from 'lucide-react'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import { useFormContext } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'

const QuestionAnswer = ({
  answer,
  index,
  name,
  orderAnswers,
  questionType,
  readOnly = false,
  remove,
}) => {
  // Form
  const { control, watch } = useFormContext()

  const hasAnswerControl = questionType !== 'Numeric' && questionType !== 'True/False'
  const hasCorrectAnswer = watch('answers')?.some((a) => a.isCorrect)

  // Drag & Drop
  const [{ opacity }, drop] = useDrop({
    accept: 'answer',
    drop({ answer: draggedAnswer, index: draggedIndex }) {
      if (draggedAnswer.id !== answer.id) {
        orderAnswers(draggedIndex, index)
      }
    },
  })
  const [{ handlerId }, drag] = useDrag(
    () => ({
      type: 'answer',
      item: { answer, index },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [answer, index],
  )

  return (
    <div
      data-handler-id={handlerId}
      ref={drop}
      key={answer.id}
      className="flex flex-row items-center gap-2"
      style={{
        opacity,
      }}
    >
      {hasAnswerControl && (
        <Button variant="plain" type="button" onClick={() => remove(index)} className="!p-0">
          <CircleX className="text-destructive focus-visible:ring-destructive/20 h-5 w-5" />
        </Button>
      )}

      {questionType !== 'Numeric' && (
        <div ref={drag}>
          <Grip className="text-muted-foreground mx-auto h-6 w-6 cursor-move" />
        </div>
      )}

      <FormField
        control={control}
        name={`${name}.title`}
        render={({ field: answerField }) => (
          <FormItem className="w-full flex-1">
            <FormControl>
              <Input
                type={questionType === 'Numeric' ? 'number' : 'text'}
                value={
                  questionType === 'Numeric' ? parseFloat(answerField.value) : answerField.value
                }
                {...answerField}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {questionType !== 'Numeric' && (
        <FormField
          control={control}
          name={`${name}.isCorrect`}
          render={({ field: correctField }) => (
            <FormItem className="flex flex-row items-center">
              <FormControl>
                <Checkbox
                  checked={correctField.value}
                  disabled={
                    readOnly ||
                    (questionType === 'Single Choice' && hasCorrectAnswer && !correctField.value)
                  }
                  onCheckedChange={(checked) => correctField.onChange(checked)}
                />
              </FormControl>
              <FormLabel className="text-xs">Correct Answer</FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}

QuestionAnswer.propTypes = {
  answer: PropTypes.object.isRequired,
  hasCorrectAnswer: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  orderAnswers: PropTypes.func.isRequired,
  questionType: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  remove: PropTypes.func.isRequired,
}

export default QuestionAnswer
